import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload, SagaPayloadwithoutRequestData } from '../api.types';
import {
  CreateSystemUserRequest,
  CreateSystemUserResponse,
  InitiatePasswordResetRequest,
  ResetPasswordRequest,
  UpdateSystemUserRequest,
  ViewSystemUserRequest,
  ViewSystemUserResponse
} from './system-user.types';

import {
  CREATING_SYSTEM_USER,
  DELETING_ACCOUNT,
  INITIATING_PASSWORD_RESET,
  RESETTING_PASSWORD,
  RETRIEVING_SYSTEM_USER,
  UPDATING_SYSTEM_USER
} from './system-user.constants';

import {
  requestCreateSystemUser,
  requestDeleteAccount,
  requestInitiatePasswordReset,
  requestResetPassword,
  requestUpdateSystemUser,
  requestViewSystemUser
} from './system-user.actions';

import {
  getCreateSystemUserRequest,
  getDeleteAccountRequest,
  getInitiatePasswordResetRequest,
  getResetPasswordRequest,
  getUpdateSystemUserRequest,
  getViewSystemUserRequest
} from './system-user.endpoints';

import { addSpinner, addSystemNotice, removeSpinner, setUser } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';
import { retrieveFromLocalStorage } from '../../utils/util';

export function* performCreateSystemUserRequest({ payload }: SagaPayload<CreateSystemUserRequest, string>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_SYSTEM_USER));

    const [endpoint, options] = getCreateSystemUserRequest(requestData);

    const { data }: AxiosResponse<CreateSystemUserResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.programmeName);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_SYSTEM_USER));
  }
}

export function* watchForCreateSystemUserRequest() {
  yield takeLatest(requestCreateSystemUser.type, performCreateSystemUserRequest);
}

export function* performViewSystemUserRequest({ payload }: SagaPayload<ViewSystemUserRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_SYSTEM_USER));

    const [endpoint, options] = getViewSystemUserRequest(requestData);

    const { data }: AxiosResponse<ViewSystemUserResponse> = yield call(axios, endpoint, options);

    yield put(setUser(data.user));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_SYSTEM_USER));
  }
}

export function* watchForViewSystemUserRequest() {
  yield takeLatest(requestViewSystemUser.type, performViewSystemUserRequest);
}

export function* performUpdateSystemUserRequest({ payload }: SagaPayload<UpdateSystemUserRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_SYSTEM_USER));

    const [endpoint, options] = getUpdateSystemUserRequest(requestData);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewSystemUser({ requestData: { systemUserExternalReference: requestData.externalReference } }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_SYSTEM_USER));
  }
}

export function* watchForUpdateSystemUserRequest() {
  yield takeLatest(requestUpdateSystemUser.type, performUpdateSystemUserRequest);
}

export function* performInitiatePasswordResetRequest({ payload }: SagaPayload<InitiatePasswordResetRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(INITIATING_PASSWORD_RESET));

    const [endpoint, options] = getInitiatePasswordResetRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(INITIATING_PASSWORD_RESET));
  }
}

export function* watchForInitiatePasswordResetRequest() {
  yield takeLatest(requestInitiatePasswordReset.type, performInitiatePasswordResetRequest);
}

export function* performResetPasswordRequest({ payload }: SagaPayload<ResetPasswordRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RESETTING_PASSWORD));

    const [endpoint, options] = getResetPasswordRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RESETTING_PASSWORD));
  }
}

export function* watchForResetPasswordRequest() {
  yield takeLatest(requestResetPassword.type, performResetPasswordRequest);
}

export function* performDeleteAccountRequest({ payload }: SagaPayloadwithoutRequestData) {
  const { onSuccess } = payload;

  try {
    yield put(addSpinner(DELETING_ACCOUNT));

    const systemUserExternalReference: string = retrieveFromLocalStorage('user');
    const [endpoint, options] = getDeleteAccountRequest({ systemUserExternalReference });

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DELETING_ACCOUNT));
  }
}

export function* watchForDeleteAccountRequest() {
  yield takeLatest(requestDeleteAccount.type, performDeleteAccountRequest);
}

export default function* systemUserSaga() {
  yield all([
    watchForCreateSystemUserRequest(),
    watchForViewSystemUserRequest(),
    watchForUpdateSystemUserRequest(),
    watchForInitiatePasswordResetRequest(),
    watchForResetPasswordRequest(),
    watchForDeleteAccountRequest()
  ]);
}
